import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	Appointment,
	AppointmentRequest,
	AppointmentResponse,
} from 'src/shared/models/appointment.model';
import {
	Dependent,
	DependentRequest,
	DependentResponse,
} from 'src/shared/models/dependent.model';
import { TravelAttachmentRequest } from 'src/shared/models/travel-attachment';
import {
	TravelComment,
	TravelCommentResponse,
} from 'src/shared/models/travel-comment.model';
import {
	TravelPurpose,
	TravelPurposeResponse,
} from 'src/shared/models/travel-purpose.model';
import {
	TravelQueue,
	TravelQueueResponse,
	TravelQueuesResponse,
} from 'src/shared/models/travel-queue.model';
import { environment } from '../../environments/environment';
import { ApprovalRequest } from '../../shared/models/approval-request.model';
import {
	Approval,
	ApprovalsResponse,
} from '../../shared/models/approval.model';
import {
	FinanceChargedDepartment,
	FinanceChargedDepartmentsResponse,
} from '../../shared/models/finance-charged-department.model';
import {
	TravelItinerarySearch,
	TravelItinerarySearchResponse,
} from '../../shared/models/travel-itinerary-search.model';
import { TravelItinerarySummary } from '../../shared/models/travel-itinerary-summary.model';
import {
	CanEditItineraryResponse,
	TravelItinerary,
	TravelItineraryRequest,
	TravelItineraryResponse,
} from '../../shared/models/travel-itinerary.model';
import { EmployeesResponse, User } from '../../shared/models/user.model';
import { BackendService } from './backend.service';

const API_URL = `${environment.apiUrl}/web/travel`;

export const APPROVALS_URL = `${API_URL}/approvals`;
export const APPROVALS_RESPONSE = 'Fetched Approvals';

export const FINANCE_CHARGED_DEPARTMENTS_URL = `${API_URL}/financechargeddepartments`;
export const FINANCE_CHARGED_DEPARTMENTS_RESPONSE =
	'Fetched Finance Changed Departments';

export const APPROVE_URL = `${API_URL}/approve`;
export const APPROVE_RESPONSE = 'Travel Request Approved';

export const DENY_URL = `${API_URL}/deny`;
export const DENY_RESPONSE = 'Travel Request Denied';

export const ITINERARY_URL = `${API_URL}/itinerary`;
export const ITINERARY_RESPONSE = 'Fetched Itinerary';

export const CAN_EDIT_URL = `${API_URL}/canedit`;
export const CAN_EDIT_RESPONSE = 'Fetched Permission';

export const RECENT_APPROVALS_URL = `${API_URL}/recentapprovals`;
export const RECENT_APPROVALS_RESPONSE = 'Fetched Recent Approvals';

export const TRAVEL_QUEUE_URL = `${API_URL}/travelqueue`;
export const TRAVEL_QUEUE_RESPONSE = 'Fetched Travel Queue';

export const TRAVEL_QUEUES_URL = `${API_URL}/travelqueues`;
export const TRAVEL_QUEUES_RESPONSE = 'Fetched Travel Queues';

export const ADMINISTRATIVE_QUEUE_URL = `${API_URL}/administrativequeue`;
export const ADMINISTRATIVE_QUEUE_RESPONSE = 'Fetched Administrative Queue';

export const TRAVEL_ADMIN_QUEUE_URL = `${API_URL}/traveladminqueue`;
export const TRAVEL_ADMIN_QUEUE_RESPONSE = 'Fetched Travel Admin Queue';

export const MANAGER_QUEUE_URL = `${API_URL}/managerqueue`;
export const MANAGER_QUEUE_RESPONSE = 'Fetched Manager Queue';

export const MY_TRAVEL_URL = `${API_URL}/mytravel`;
export const MY_TRAVEL_RESPONSE = 'Fetched My Travel';

export const SEARCH_URL = `${API_URL}/search`;
export const SEARCH_RESPONSE = 'Fetched Search';

export const MY_EMPLOYEES_URL = `${API_URL}/myemployees`;
export const MY_EMPLOYEES_RESPONSE = 'Fetched My Employees';

export const TRAVELERS_URL = `${API_URL}/travelers`;
export const TRAVELERS_RESPONSE = 'Fetched Travelers';

export const DEPENDENTS_URL = `${API_URL}/dependents`;
export const DEPENDENTS_RESPONSE = 'Fetched Dependents';

export const APPOINTMENTS_URL = `${API_URL}/appointments`;
export const APPOINTMENTS_RESPONSE = 'Fetched Appointments';

export const TRAVEL_PURPOSES_URL = `${API_URL}/purposes`;
export const TRAVEL_PURPOSES_RESPONSE = 'Fetched Travel Purposes';

export const ADD_COMMENT_URL = `${API_URL}/addcomment`;
export const ADD_COMMENT_RESPONSE = 'Comment Added';

export const CREATE_ITINERARY_URL = `${API_URL}/createitinerary`;
export const CREATE_ITINERARY_RESPONSE = 'Created Travel Itinerary';

export const UPDATE_ITINERARY_URL = `${API_URL}/updateitinerary`;
export const UPDATE_ITINERARY_RESPONSE = 'Updated Travel Itinerary';

export const UPDATE_TRAVEL_DEPENDENTS_URL = `${API_URL}/updatetraveldependents`;
export const UPDATE_TRAVEL_DEPENDENTS_RESPONSE = 'Updated Travel Dependents';

export const UPDATE_TRAVEL_PARTNERS_URL = `${API_URL}/updatetravelpartners`;
export const UPDATE_TRAVEL_PARTNERS_RESPONSE = 'Updated Travel Partners';

export const UPDATE_TRANSPORTATION_URL = `${API_URL}/updatetransportation`;
export const UPDATE_TRANSPORTATION_RESPONSE = 'Updated Transportation';

export const UPDATE_LODGINGS_URL = `${API_URL}/updatelodgings`;
export const UPDATE_LODGINGS_RESPONSE = 'Updated Lodgings';

export const UPDATE_OTHER_COSTS_URL = `${API_URL}/updateothercosts`;
export const UPDATE_OTHER_COSTS_RESPONSE = 'Updated Other Costs';

export const UPDATE_FINANCE_URL = `${API_URL}/updatefinance`;
export const UPDATE_FINANCE_RESPONSE = 'Updated Finance';

export const UPLOAD_ATTACHMENT_URL = `${API_URL}/uploadattachment`;
export const UPLOAD_ATTACHMENT_RESPONSE = 'Uploaded Attachment';

export const DELETE_ATTACHMENT_URL = `${API_URL}/deleteattachment`;
export const DELETE_ATTACHMENT_RESPONSE = 'Deleted Attachment';

export const SUBMIT_URL = `${API_URL}/submit`;
export const SUBMIT_RESPONSE = 'Submitted';

export const CANCEL_URL = `${API_URL}/cancel`;
export const CANCEL_RESPONSE = 'Cancelled';

export const DELETE_URL = `${API_URL}/delete`;
export const DELETE_RESPONSE = 'Deleted';

@Injectable({
	providedIn: 'root',
})
export class TravelBackendService {
	private backend = inject(BackendService);

	/**
	 * Approvals
	 * ===========
	 * API Endpoint: /web/travel/approvals
	 *
	 * Returns a set of Approvals for the logged in user.
	 */
	approvals(): Observable<Approval[]> {
		return this.backend
			.post(APPROVALS_URL, APPROVALS_RESPONSE, {})
			.pipe(
				map((response: ApprovalsResponse) =>
					response.approvals.map((approval) =>
						Approval.fromJSON(approval)
					)
				)
			);
	}

	/**
	 * FinanceChargedDepartments
	 * ==========================
	 * API Endpoint: /web/travel/financechargeddepartments
	 *
	 * Returns an array of FinanceChargedDepartment objects.
	 */
	financeChargedDepartments(): Observable<FinanceChargedDepartment[]> {
		return this.backend
			.post(
				FINANCE_CHARGED_DEPARTMENTS_URL,
				FINANCE_CHARGED_DEPARTMENTS_RESPONSE,
				{}
			)
			.pipe(
				map((response: FinanceChargedDepartmentsResponse) => {
					return response.financeChargedDepartments.map((json) =>
						FinanceChargedDepartment.fromJSON(json)
					);
				})
			);
	}

	/**
	 * Approve
	 * ========
	 * API Endpoint: /web/travel/approve
	 * JSON Parameters: id, comment, chargedDepartment, chartString, chargedProject
	 *
	 * Approves a Travel Itinerary (has Finance Approval data too)
	 */
	approve(request: ApprovalRequest): Observable<void> {
		return this.backend.post(APPROVE_URL, APPROVE_RESPONSE, request).pipe(
			map((_response) => {
				return;
			})
		);
	}

	/**
	 * Deny
	 * =====
	 * API Endpoint: /web/travel/deny
	 * JSON Parameters: id, comment
	 *
	 * Denies a Travel Itinerary
	 */
	deny(request: ApprovalRequest): Observable<void> {
		return this.backend.post(DENY_URL, DENY_RESPONSE, request).pipe(
			map((_response) => {
				return;
			})
		);
	}

	/**
	 * Itinerary
	 * ==========
	 * API Endpoint: /web/travel/itinerary
	 * JSON Parameters: id
	 *
	 * Returns a Travel Itinerary Response
	 */
	itinerary(
		request: TravelItineraryRequest
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(ITINERARY_URL, ITINERARY_RESPONSE, request)
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * CanEdit (Itinerary)
	 * ====================
	 * API Endpoint: /web/travel/canedit
	 * JSON Parameters: id
	 *
	 * Returns a CanEditItineraryResponse
	 */
	canEdit(
		request: TravelItineraryRequest
	): Observable<CanEditItineraryResponse> {
		return this.backend.post(CAN_EDIT_URL, CAN_EDIT_RESPONSE, request).pipe(
			map((response: CanEditItineraryResponse) => {
				return response;
			})
		);
	}

	/**
	 * Recent Approvals
	 * =================
	 * API Endpoint: /web/travel/recentapprovals
	 *
	 * Returns a set of Recent Approvals for the logged in user.
	 */
	recentApprovals(): Observable<Approval[]> {
		return this.backend
			.post(RECENT_APPROVALS_URL, RECENT_APPROVALS_RESPONSE, {})
			.pipe(
				map((response: ApprovalsResponse) =>
					response.approvals.map((approval) =>
						Approval.fromJSON(approval)
					)
				)
			);
	}

	/**
	 * Dashboard Travel Queue
	 * =======================
	 * API Endpoint: /web/travel/travelqueue
	 *
	 * Returns either the Administrative or Travel Admin queue depending on roles
	 */
	dashboardTravelQueue(): Observable<TravelItinerarySummary[]> {
		return this.backend
			.post(TRAVEL_QUEUE_URL, TRAVEL_QUEUE_RESPONSE, {})
			.pipe(
				map((response: TravelQueueResponse) =>
					response.queue.map((summary) =>
						TravelItinerarySummary.fromJSON(summary)
					)
				)
			);
	}

	/**
	 * All Travel Queues
	 * ==================
	 * API Endpoint: /web/travel/travelqueues
	 *
	 * Returns all Travel queues, depending on roles
	 */
	allTtravelQueues(): Observable<TravelQueuesResponse> {
		return this.backend
			.post(TRAVEL_QUEUES_URL, TRAVEL_QUEUES_RESPONSE, {})
			.pipe(map((r: TravelQueuesResponse) => r));
	}

	/**
	 * Administrative Queue
	 * =====================
	 * API Endpoint: /web/travel/administrativequeue
	 * JSON Parameters: page
	 *
	 * Returns the Administrative (Dean & Finance) queue, depending on roles
	 */
	administrativeQueue(page: number): Observable<TravelQueue> {
		return this.backend
			.post(ADMINISTRATIVE_QUEUE_URL, ADMINISTRATIVE_QUEUE_RESPONSE, {
				page: page,
			})
			.pipe(
				map((r: TravelQueuesResponse) =>
					TravelQueue.fromJSON(r.administrative)
				)
			);
	}

	/**
	 * Travel Admin Queue
	 * ===================
	 * API Endpoint: /web/travel/traveladminqueue
	 * JSON Parameters: page
	 *
	 * Returns the Travel Admin queue, depending on roles
	 */
	travelAdminQueue(page: number): Observable<TravelQueue> {
		return this.backend
			.post(TRAVEL_ADMIN_QUEUE_URL, TRAVEL_ADMIN_QUEUE_RESPONSE, {
				page: page,
			})
			.pipe(
				map((r: TravelQueuesResponse) =>
					TravelQueue.fromJSON(r.travelAdmin)
				)
			);
	}

	/**
	 * Manager Queue
	 * ==============
	 * API Endpoint: /web/travel/managerqueue
	 * JSON Parameters: page
	 *
	 * Returns the Manager Queue, depending on roles & only for the currently logged in user
	 */
	managerQueue(page: number): Observable<TravelQueue> {
		return this.backend
			.post(MANAGER_QUEUE_URL, MANAGER_QUEUE_RESPONSE, {
				page: page,
			})
			.pipe(
				map((r: TravelQueuesResponse) =>
					TravelQueue.fromJSON(r.manager)
				)
			);
	}

	/**
	 * My Travel
	 * ==========
	 * API Endpoint: /web/travel/mytravel
	 * JSON Parameters: page
	 *
	 * Returns the travel queue for the currently logged in user (mixed itinerary types)
	 */
	myTravel(page: number): Observable<TravelQueue> {
		return this.backend
			.post(MY_TRAVEL_URL, MY_TRAVEL_RESPONSE, { page: page })
			.pipe(
				map((r: TravelQueuesResponse) =>
					TravelQueue.fromJSON(r.myTravel)
				)
			);
	}

	/**
	 * Search
	 * =======
	 * API Endpoint: /web/travel/search
	 * JSON Parameters: Application_Model_TravelItinerarySearch
	 *
	 * Returns the result of the search query based upon the provided parameters & the currently logged in user
	 */
	search(
		search: TravelItinerarySearch
	): Observable<TravelItinerarySearchResponse> {
		return this.backend
			.post(SEARCH_URL, SEARCH_RESPONSE, search)
			.pipe(map((r: TravelItinerarySearchResponse) => r));
	}

	/**
	 * My Employees
	 * =============
	 * API Endpoint: /web/travel/myemployees
	 *
	 * Returns an array of users that are employees of the currently logged in user
	 */
	myEmployees(): Observable<User[]> {
		return this.backend
			.post(MY_EMPLOYEES_URL, MY_EMPLOYEES_RESPONSE, {})
			.pipe(
				map((r: EmployeesResponse) => {
					return r.employees.map((u) => User.fromJSON(u));
				})
			);
	}

	/**
	 * Travelers
	 * ==========
	 * API Endpoint: /web/travel/travelers
	 *
	 * Returns an array of users that are used for creating new Travel Itineraries for the currently logged in user
	 */
	travelers(): Observable<User[]> {
		return this.backend.post(TRAVELERS_URL, TRAVELERS_RESPONSE, {}).pipe(
			map((r: EmployeesResponse) => {
				return r.employees.map((u) => User.fromJSON(u));
			})
		);
	}

	/**
	 * Dependents
	 * ===========
	 * API Endpoint: /web/travel/dependents
	 * JSON Parameters: id
	 *
	 * Returns an array of Dependents for the user of the provided `id`
	 */
	getDependents(request: DependentRequest): Observable<Dependent[]> {
		return this.backend
			.post(DEPENDENTS_URL, DEPENDENTS_RESPONSE, request)
			.pipe(
				map((r: DependentResponse) => {
					return r.dependents.map((json) => Dependent.fromJSON(json));
				})
			);
	}

	/**
	 * Appointments
	 * =============
	 * API Endpoint: /web/travel/appointments
	 * JSON Parameters: id
	 *
	 * Returns an array of Appointments for the user of the provided `id`
	 */
	getAppointments(request: AppointmentRequest): Observable<Appointment[]> {
		return this.backend
			.post(APPOINTMENTS_URL, APPOINTMENTS_RESPONSE, request)
			.pipe(
				map((response: AppointmentResponse) =>
					response.appointments.map((json) =>
						Appointment.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Travel Purposes
	 * ================
	 * API Endpoint: /web/travel/purposes
	 *
	 * Returns an array of Travel Purposes
	 */
	getTravelPurposes(): Observable<TravelPurpose[]> {
		return this.backend
			.post(TRAVEL_PURPOSES_URL, TRAVEL_PURPOSES_RESPONSE, {})
			.pipe(
				map((r: TravelPurposeResponse) =>
					r.purposes.map((json) => TravelPurpose.fromJSON(json))
				)
			);
	}

	/**
	 * Add Comment
	 * ============
	 * API Endpoint: /web/travel/addcomment
	 * JSON Parameters: id, comment
	 *
	 * Adds a comment to an existing Travel Itinerary
	 */
	addComment(comment: TravelComment): Observable<TravelComment[]> {
		return this.backend
			.post(ADD_COMMENT_URL, ADD_COMMENT_RESPONSE, {
				id: comment.itineraryId,
				comment: comment.comment,
			})
			.pipe(
				map((r: TravelCommentResponse) =>
					r.comments.map((c) => TravelComment.fromJSON(c))
				)
			);
	}

	/**
	 * Create Travel Itinerary
	 * ========================
	 * API Endpoint: /web/travel/createitinerary
	 * JSON Parameters: userId, dependentOnlyTravel, dependents, travelPartners, type,
	 * 					tripName, travelLocation, departureDate, returnDate, travelPurpose, currency,
	 * 					travelJustification, appointmentId, homeCampusVisit, teachingDaysMissed
	 *
	 * Creates a new Travel Itinerary
	 */
	createItinerary(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(CREATE_ITINERARY_URL, CREATE_ITINERARY_RESPONSE, {
				userId: itinerary.userId,
				dependentOnlyTravel: itinerary.dependentOnlyTravel,
				dependents: itinerary.dependents,
				travelPartners: itinerary.travelPartners,
				type: itinerary.type,
				tripName: itinerary.tripName,
				travelLocation: itinerary.travelLocation,
				departureDate: itinerary.departureDate,
				returnDate: itinerary.returnDate,
				travelPurpose: itinerary.travelPurpose,
				currency: itinerary.currency,
				travelJustification: itinerary.travelJustification,
				appointmentId: itinerary.appointmentId,
				homeCampusVisit: itinerary.homeCampusVisit,
				teachingDaysMissed: itinerary.teachingDaysMissed,
			})
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Update Itinerary
	 * =================
	 * API Endpoint: /web/travel/updateitinerary
	 * JSON Parameters: id, type, tripName, travelLocation, departureDate, returnDate, travelPurpose,
	 * 					currency, travelJustification, appointmentId, homeCampusVisit, teachingDaysMissed
	 *
	 * Updates the general information fields for a given Travel Itinerary
	 */
	updateItinerary(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(UPDATE_ITINERARY_URL, UPDATE_ITINERARY_RESPONSE, {
				id: itinerary.id,
				type: itinerary.type,
				tripName: itinerary.tripName,
				travelLocation: itinerary.travelLocation,
				departureDate: itinerary.departureDate,
				returnDate: itinerary.returnDate,
				travelPurpose: itinerary.travelPurpose,
				currency: itinerary.currency,
				travelJustification: itinerary.travelJustification,
				appointmentId: itinerary.appointmentId,
				homeCampusVisit: itinerary.homeCampusVisit,
				teachingDaysMissed: itinerary.teachingDaysMissed,
			})
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Update Travel Dependents
	 * =========================
	 * API Endpoint: /web/travel/updatetraveldependents
	 * JSON Parameters: id, dependentOnlyTravel, dependents
	 *
	 * Updates the Travel Dependents for a given Travel Itinerary
	 */
	updateTravelDependents(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(
				UPDATE_TRAVEL_DEPENDENTS_URL,
				UPDATE_TRAVEL_DEPENDENTS_RESPONSE,
				{
					id: itinerary.id,
					dependentOnlyTravel: itinerary.dependentOnlyTravel,
					dependents: itinerary.dependents,
				}
			)
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Update Travel Partners
	 * =========================
	 * API Endpoint: /web/travel/updatetravelpartners
	 * JSON Parameters: id, travelPartners
	 *
	 * Updates the Travel Partners for a given Travel Itinerary
	 */
	updateTravelPartners(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(UPDATE_TRAVEL_PARTNERS_URL, UPDATE_TRAVEL_PARTNERS_RESPONSE, {
				id: itinerary.id,
				travelPartners: itinerary.travelPartners,
			})
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Update Transportation
	 * ======================
	 * API Endpoint: /web/travel/updatetransportation
	 * JSON Parameters: id, transportationNotes, transportationGroups
	 *
	 * Updates the Transportation Groups and Legs for a given Travel Itinerary
	 */
	updateTransportation(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(UPDATE_TRANSPORTATION_URL, UPDATE_TRANSPORTATION_RESPONSE, {
				id: itinerary.id,
				transportationNotes: itinerary.transportationNotes,
				transportationGroups: itinerary.transportationGroups,
			})
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Update Lodgings
	 * ================
	 * API Endpoint: /web/travel/updatelodgings
	 * JSON Parameters: id, lodgingNotes, lodgings
	 *
	 * Updates the Lodgings for a given Travel Itinerary
	 */
	updateLodgings(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(UPDATE_LODGINGS_URL, UPDATE_LODGINGS_RESPONSE, {
				id: itinerary.id,
				lodgingNotes: itinerary.lodgingNotes,
				lodgings: itinerary.lodgings,
			})
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Update Other Costs
	 * ===================
	 * API Endpoint: /web/travel/updateothercosts
	 * JSON Parameters: id, otherCostNotes, otherCosts
	 *
	 * Updates the Other Costs for a given Travel Itinerary
	 */
	updateOtherCosts(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(UPDATE_OTHER_COSTS_URL, UPDATE_OTHER_COSTS_RESPONSE, {
				id: itinerary.id,
				otherCostNotes: itinerary.otherCostNotes,
				otherCosts: itinerary.otherCosts,
			})
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Update Finance
	 * ===============
	 * API Endpoint: /web/travel/updatefinance
	 * JSON Parameters: id, chargedDepartment, chartString, chargedProject
	 *
	 * Updates the Finance values for a given Travel Itinerary
	 */
	updateFinance(
		itinerary: TravelItinerary
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(UPDATE_FINANCE_URL, UPDATE_FINANCE_RESPONSE, {
				id: itinerary.id,
				chargedDepartment: itinerary.chargedDepartment,
				chartString: itinerary.chartString,
				chargedProject: itinerary.chargedProject,
			})
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Upload Attachment
	 * ==================
	 * API Endpoint: /web/travel/uploadattachment
	 * JSON Parameters: TravelAttachmentRequest
	 *
	 * Uploads an attachment for a given Travel Itinerary
	 * Intended to be used with attachments encoded with FileReader.readAsDataURL(), and added as a JSON parameter.
	 */
	uploadAttachment(
		request: TravelAttachmentRequest
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(UPLOAD_ATTACHMENT_URL, UPLOAD_ATTACHMENT_RESPONSE, request)
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Delete Attachment
	 * ==================
	 * API Endpoint: /web/travel/deleteattachment
	 * JSON Parameters: id, itineraryId
	 *
	 * Deletes an attachment for a given Travel Itinerary
	 */
	deleteAttachment(
		request: TravelAttachmentRequest
	): Observable<TravelItineraryResponse> {
		return this.backend
			.post(DELETE_ATTACHMENT_URL, DELETE_ATTACHMENT_RESPONSE, request)
			.pipe(
				map((response: TravelItineraryResponse) => {
					return response;
				})
			);
	}

	/**
	 * Submit
	 * =======
	 * API Endpoint: /web/travel/submit
	 * JSON Parameters: id
	 *
	 * Submits a given Travel Itinerary
	 */
	submit(itinerary: TravelItinerary): Observable<TravelItineraryResponse> {
		return this.backend.post(SUBMIT_URL, SUBMIT_RESPONSE, itinerary).pipe(
			map((response: TravelItineraryResponse) => {
				return response;
			})
		);
	}

	/**
	 * Cancel
	 * =======
	 * API Endpoint: /web/travel/cancel
	 * JSON Parameters: id
	 *
	 * Cancels a given Travel Itinerary
	 */
	cancel(itinerary: TravelItinerary): Observable<TravelItineraryResponse> {
		return this.backend.post(CANCEL_URL, CANCEL_RESPONSE, itinerary).pipe(
			map((response: TravelItineraryResponse) => {
				return response;
			})
		);
	}

	/**
	 * Delete
	 * =======
	 * API Endpoint: /web/travel/delete
	 * JSON Parameters: id
	 *
	 * Deletes a given Travel Itinerary
	 */
	delete(itinerary: TravelItinerary): Observable<TravelItineraryResponse> {
		return this.backend.post(DELETE_URL, DELETE_RESPONSE, itinerary).pipe(
			map((response: TravelItineraryResponse) => {
				return response;
			})
		);
	}
}
